import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, UserPlus, Trash2, Search, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAdmins() {
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/users');
      const allUsers = response.data;
      setUsers(allUsers);
      setAdmins(allUsers.filter(u => u.role === 'admin'));
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchEmail.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    const results = users.filter(u => 
      u.email.toLowerCase().includes(searchEmail.toLowerCase()) ||
      u.name?.toLowerCase().includes(searchEmail.toLowerCase())
    );
    setSearchResults(results);
    setSearching(false);
  };

  const makeAdmin = async (userId, email) => {
    try {
      await apiClient.put(`/admin/users/${userId}/role`, { role: 'admin' });
      toast.success(`${email} is now an admin`);
      fetchUsers();
      setSearchEmail('');
      setSearchResults([]);
    } catch (error) {
      toast.error('Failed to make user admin');
    }
  };

  const removeAdmin = async (userId, email) => {
    if (!window.confirm(`Remove admin privileges from ${email}?`)) return;
    
    try {
      await apiClient.put(`/admin/users/${userId}/role`, { role: 'user' });
      toast.success(`${email} is no longer an admin`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to remove admin');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Admin Management</h1>
          <p className="text-white/60 mt-2">Add or remove administrators</p>
        </div>

        {/* Add New Admin */}
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus size={20} className="text-violet-400" />
              Add New Admin
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Input
                placeholder="Search by email or name..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-white/5 border-white/10"
              />
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-sm text-white/60">Found {searchResults.length} user(s):</p>
                {searchResults.map((user) => (
                  <div 
                    key={user.user_id}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                          <Shield size={18} className="text-violet-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{user.name || 'No name'}</p>
                        <p className="text-sm text-white/60">{user.email}</p>
                      </div>
                    </div>
                    {user.role === 'admin' ? (
                      <span className="text-green-400 text-sm flex items-center gap-1">
                        <CheckCircle size={14} />
                        Already Admin
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => makeAdmin(user.user_id, user.email)}
                        className="bg-violet-600 hover:bg-violet-500"
                      >
                        <Shield size={14} className="mr-1" />
                        Make Admin
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {searchEmail && searchResults.length === 0 && !searching && (
              <div className="text-center py-4 text-white/50">
                <AlertCircle size={24} className="mx-auto mb-2" />
                <p>No users found matching "{searchEmail}"</p>
                <p className="text-sm">Make sure the user has signed up first</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Admins */}
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield size={20} className="text-green-400" />
              Current Administrators ({admins.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-violet-400" size={32} />
              </div>
            ) : admins.length === 0 ? (
              <p className="text-center text-white/50 py-8">No administrators found</p>
            ) : (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div 
                    key={admin.user_id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition"
                  >
                    <div className="flex items-center gap-4">
                      {admin.picture ? (
                        <img src={admin.picture} alt="" className="w-12 h-12 rounded-full border-2 border-green-500" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center border-2 border-green-500">
                          <Shield size={20} className="text-green-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-lg">{admin.name || 'Admin'}</p>
                        <p className="text-white/60">{admin.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAdmin(admin.user_id, admin.email)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 size={18} className="mr-1" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
